import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { StatCard, DailyBars, DailyArea, FortuneWheel, PercentSlider } from "./components/GameVisuals";
import {
  FiRefreshCw,
  FiSettings,
  FiList,
  FiSave,
  FiSearch,
  FiInfo,
  FiCopy,
  FiAlertTriangle,
} from "react-icons/fi";

const MAIN_TABS = [
  { key: "settings", label: "Game Settings", icon: FiSettings },
  { key: "history", label: "Game History & Logs", icon: FiList },
];

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "disqualified", label: "Disqualified" },
  { key: "started", label: "Started" },
];

const STATUS_BADGE = {
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  disqualified: "bg-rose-100 text-rose-700 border-rose-200",
  started: "bg-amber-100 text-amber-700 border-amber-200",
  expired: "bg-gray-100 text-gray-500 border-gray-200",
};

/** "45s", "2m 05s", "1h 03m" — how long a session ran, start to end. */
const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m`;
};

/** "10:04:12 AM" — enough to line overlapping sessions up against each other. */
const formatClock = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
};

/**
 * A start or end time in the history list: the time to the second, with the
 * date beneath. Seconds matter here — they are what lines two overlapping
 * sessions up against each other.
 */
const TimeCell = ({ value, empty = "—" }) => {
  const d = value ? new Date(value) : null;
  if (!d || isNaN(d.getTime())) return <span className="text-xs text-gray-400 whitespace-nowrap">{empty}</span>;
  return (
    <>
      <p className="text-gray-700 tabular-nums whitespace-nowrap">{formatClock(value)}</p>
      <p className="text-[11px] text-gray-400 whitespace-nowrap">
        {d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}
      </p>
    </>
  );
};

const Field = ({ label, value, onChange, hint, min, max }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={onChange}
      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
    />
    {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

/**
 * Tap Game, Boom Cards and 2048 render off this one component — same settings +
 * history shape, only the API segment, copy, and score-column label differ.
 * See src/utils/miniGameApi.js for the backend calls.
 */
const MiniGamePage = ({ title, selfLabel, description, icon: Icon, api, switchTo, scoreLabel }) => {
  const [mainTab, setMainTab] = useState("settings");

  // `switchTo` was a single {to,label} back when there were only two games. It
  // now takes a list; the old single-object form is still accepted so any
  // caller still passing one keeps working.
  const switchTargets = Array.isArray(switchTo) ? switchTo : switchTo ? [switchTo] : [];

  // ── Settings ─────────────────────────────────────────────────────────
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving, setSaving] = useState(false);

  // ── Daily trends ─────────────────────────────────────────────────────
  const [trends, setTrends] = useState(null);
  const [trendDays, setTrendDays] = useState(7);
  // A spin the admin triggered from the simulator, so the wheel can point
  // at it. Null means the wheel sits still.
  const [testSpin, setTestSpin] = useState(null);
  const [spinning, setSpinning] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const res = await api.fetchSettings();
      setSettings(res.data);
      setForm({
        gameTime: res.data.gameTime,
        minPoints: res.data.minPoints,
        maxPoints: res.data.maxPoints,
        cooldownSeconds: res.data.cooldownSeconds,
        isActive: Boolean(res.data.isActive),
        // Only meaningful on a formula game; harmless on the others, and the
        // save strips whichever set does not apply.
        scoreUnit: res.data.scoreUnit ?? 100,
        pointsPerUnit: res.data.pointsPerUnit ?? 2,
        maxPointsPerRound: res.data.maxPointsPerRound ?? 1000,
        maxScorePerRound: res.data.maxScorePerRound ?? 200000,
        minPlaySeconds: res.data.minPlaySeconds ?? 10,
        // Slots keeps its tiers and odds in `config`, so they are lifted
        // into flat form fields here and folded back on save.
        jackpotPoints: res.data.config?.tiers?.jackpotPoints ?? 200,
        tripleBarPoints: res.data.config?.tiers?.tripleBarPoints ?? 100,
        tripleCherryPoints: res.data.config?.tiers?.tripleCherryPoints ?? 50,
        otherTriplePoints: res.data.config?.tiers?.otherTriplePoints ?? 20,
        match2Points: res.data.config?.tiers?.match2Points ?? 10,
        jackpotPercentage: res.data.config?.pointProbabilities?.jackpotPercentage ?? 1,
        tripleBarPercentage: res.data.config?.pointProbabilities?.tripleBarPercentage ?? 2,
        tripleCherryPercentage: res.data.config?.pointProbabilities?.tripleCherryPercentage ?? 5,
        otherTriplePercentage: res.data.config?.pointProbabilities?.otherTriplePercentage ?? 5,
        match2Percentage: res.data.config?.pointProbabilities?.match2Percentage ?? 50,
        // Both the slots game and the wheel call their miss chance
        // `lossPercentage`, so one field serves both. The fallback differs per
        // model, and only applies to a game that has never been configured.
        lossPercentage:
          res.data.config?.pointProbabilities?.lossPercentage ??
          (res.data.scoringModel === "spin_wheel" ? 30 : 37),
        totalSpins: res.data.config?.totalSpins ?? 5,
        // The wheel keeps its slices and odds in `config` too.
        slices: res.data.config?.slices ?? [0, 10, 0, 50, 0, 100, 0, 500, 0, 1000],
        spinTime: res.data.config?.spinTime ?? 5,
        tenPointsPercentage: res.data.config?.pointProbabilities?.tenPointsPercentage ?? 56,
        fiftyPointsPercentage: res.data.config?.pointProbabilities?.fiftyPointsPercentage ?? 10,
        hundredPointsPercentage: res.data.config?.pointProbabilities?.hundredPointsPercentage ?? 2,
        fiveHundredPointsPercentage: res.data.config?.pointProbabilities?.fiveHundredPointsPercentage ?? 1,
        thousandPointsPercentage: res.data.config?.pointProbabilities?.thousandPointsPercentage ?? 1,
      });
    } catch (err) {
      toast.error(err.message || "Failed to load game settings");
    } finally {
      setLoadingSettings(false);
    }
  }, [api]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadTrends = useCallback(async () => {
    // Older deployments have no /trends route; the charts simply stay
    // hidden rather than the page failing to load.
    if (typeof api.fetchTrends !== "function") return;
    try {
      const res = await api.fetchTrends(trendDays);
      setTrends(res);
    } catch (err) {
      setTrends(null);
    }
  }, [api, trendDays]);

  useEffect(() => {
    loadTrends();
  }, [loadTrends]);

  // Which half of the settings screen applies. Driven by the game's own
  // scoringModel rather than by its key, so a second formula game needs no
  // change here.
  const isFormulaGame = settings?.scoringModel === "score_formula";
  const isSlotsGame = settings?.scoringModel === "spin_reels";
  const isWheelGame = settings?.scoringModel === "spin_wheel";

  // Tap Game and Boom Cards: a random target inside a configured range.
  // Anything that is not one of the three specialised models is treated as
  // roll-based, so a future game defaults to the simplest settings screen.
  const isRollBasedGame = !isFormulaGame && !isSlotsGame && !isWheelGame;

  // Shown so an admin can see the prize odds add up as intended. They are
  // weights rather than strict percentages, so 100 is a guide, not a rule.
  const wheelOddsTotal = useMemo(() => {
    const keys = ["lossPercentage", "tenPointsPercentage", "fiftyPointsPercentage",
      "hundredPointsPercentage", "fiveHundredPointsPercentage", "thousandPointsPercentage"];
    return keys.reduce((sum, k) => sum + (Number(form?.[k]) || 0), 0);
  }, [form]);

  // The odds are weights rather than strict percentages, but showing the
  // total lets an admin see at a glance whether they add up as intended.
  const oddsTotal = useMemo(() => {
    const keys = ["jackpotPercentage", "tripleBarPercentage", "tripleCherryPercentage",
      "otherTriplePercentage", "match2Percentage", "lossPercentage"];
    return keys.reduce((sum, k) => sum + (Number(form?.[k]) || 0), 0);
  }, [form]);
  const isNoClockFormulaGame = selfLabel === "2048" || selfLabel === "Fruit Cut";

  // A worked example under the current rate, so the effect of an edit is
  // visible before saving. Pinned to the score that earns the cap, or 15,000
  // when there is no cap to aim at.
  const sampleScore = useMemo(() => {
    const unit = Number(form?.scoreUnit) || 0;
    const perUnit = Number(form?.pointsPerUnit) || 0;
    const cap = Number(form?.maxPointsPerRound) || 0;
    if (unit > 0 && perUnit > 0 && cap > 0) return Math.floor((cap / perUnit) * unit);
    return 15000;
  }, [form?.scoreUnit, form?.pointsPerUnit, form?.maxPointsPerRound]);

  const samplePoints = useMemo(() => {
    const unit = Number(form?.scoreUnit) || 0;
    const perUnit = Number(form?.pointsPerUnit) || 0;
    if (unit <= 0) return 0;
    return Math.floor(sampleScore / unit) * perUnit;
  }, [sampleScore, form?.scoreUnit, form?.pointsPerUnit]);

  const WHEEL_ODDS = [
    "lossPercentage",
    "tenPointsPercentage",
    "fiftyPointsPercentage",
    "hundredPointsPercentage",
    "fiveHundredPointsPercentage",
    "thousandPointsPercentage",
  ];

  /**
   * Scale the six odds so they total exactly 100, keeping their relative
   * weight. Rounding is absorbed by the largest tier, so the displayed
   * numbers always add up rather than landing on 99 or 101.
   */
  const autoBalance = () => {
    const values = WHEEL_ODDS.map((k) => Number(form[k]) || 0);
    const total = values.reduce((a, b) => a + b, 0);
    if (total <= 0) {
      toast.error("Set at least one chance above zero before balancing.");
      return;
    }
    const scaled = values.map((v) => Math.round((v / total) * 100));
    const drift = 100 - scaled.reduce((a, b) => a + b, 0);
    const biggest = scaled.indexOf(Math.max(...scaled));
    scaled[biggest] += drift;

    const next = { ...form };
    WHEEL_ODDS.forEach((k, i) => {
      next[k] = scaled[i];
    });
    setForm(next);
    toast.success("Balanced to 100%");
  };

  /** Restore the odds the wheel shipped with. */
  const resetWheelDefaults = () => {
    setForm({
      ...form,
      lossPercentage: 30,
      tenPointsPercentage: 56,
      fiftyPointsPercentage: 10,
      hundredPointsPercentage: 2,
      fiveHundredPointsPercentage: 1,
      thousandPointsPercentage: 1,
    });
    toast.success("Default probabilities restored");
  };

  /**
   * Spin the wheel from the values on screen, without touching the server.
   *
   * Deliberately local: calling the real start endpoint would consume one of
   * the player's plays and start a cooldown just to preview a setting.
   */
  const testSpinWheel = () => {
    const slices = (form.slices || []).map(Number);
    if (!slices.length) return;

    const weights = WHEEL_ODDS.map((k) => Math.max(0, Number(form[k]) || 0));
    const tiers = [0, 10, 50, 100, 500, 1000];
    const total = weights.reduce((a, b) => a + b, 0);

    let points = 0;
    if (total > 0) {
      let roll = Math.random() * total;
      for (let i = 0; i < tiers.length; i += 1) {
        if (roll < weights[i]) {
          points = tiers[i];
          break;
        }
        roll -= weights[i];
      }
    }

    // Land on a slice that actually carries that prize; fall back to any
    // slice if the wheel has none.
    const matches = slices.map((v, i) => (v === points ? i : -1)).filter((i) => i >= 0);
    const index = matches.length
      ? matches[Math.floor(Math.random() * matches.length)]
      : Math.floor(Math.random() * slices.length);

    setSpinning(true);
    setTestSpin(index);
    setTimeout(() => {
      setSpinning(false);
      toast.success(
        slices[index] > 0 ? `Landed on slice #${index} — ${slices[index]} pts` : `Landed on slice #${index} — no win`
      );
    }, 3000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Send only the fields this game's model actually uses, so a roll-based
      // game never writes formula settings and vice versa.
      const {
        gameTime,
        cooldownSeconds,
        minPoints,
        maxPoints,
        scoreUnit,
        pointsPerUnit,
        maxPointsPerRound,
        maxScorePerRound,
        minPlaySeconds,
        jackpotPoints,
        tripleBarPoints,
        tripleCherryPoints,
        otherTriplePoints,
        match2Points,
        jackpotPercentage,
        tripleBarPercentage,
        tripleCherryPercentage,
        otherTriplePercentage,
        match2Percentage,
        lossPercentage,
        totalSpins,
        slices,
        spinTime,
        // `lossPercentage` is already destructured above: the slots game and
        // the wheel both call their miss chance by that name.
        tenPointsPercentage,
        fiftyPointsPercentage,
        hundredPointsPercentage,
        fiveHundredPointsPercentage,
        thousandPointsPercentage,
        ...shared
      } = form;
      const payload = isWheelGame
        ? {
            ...shared,
            cooldownSeconds,
            slices,
            spinTime,
            lossPercentage,
            tenPointsPercentage,
            fiftyPointsPercentage,
            hundredPointsPercentage,
            fiveHundredPointsPercentage,
            thousandPointsPercentage,
          }
        : isSlotsGame
        ? {
            ...shared,
            jackpotPoints,
            tripleBarPoints,
            tripleCherryPoints,
            otherTriplePoints,
            match2Points,
            jackpotPercentage,
            tripleBarPercentage,
            tripleCherryPercentage,
            otherTriplePercentage,
            match2Percentage,
            lossPercentage,
            totalSpins,
          }
        : isFormulaGame
        ? {
            ...shared,
            ...(isNoClockFormulaGame ? {} : { gameTime, cooldownSeconds }),
            scoreUnit,
            pointsPerUnit,
            maxPointsPerRound,
            maxScorePerRound,
            minPlaySeconds,
          }
        : { ...shared, gameTime, cooldownSeconds, minPoints, maxPoints };

      const res = await api.updateSettings(payload);
      toast.success(res.message || "Settings updated successfully");
      await loadSettings();
    } catch (err) {
      toast.error(err.message || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  // ── History ──────────────────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const searchTimer = useRef(null);
  // "" is every event; otherwise one event's id.
  const [eventFilter, setEventFilter] = useState("");
  // Only sessions the player played while another game's session was open.
  const [overlapOnly, setOverlapOnly] = useState(false);
  const [events, setEvents] = useState(null);
  // Totals for everything the filters match, not just the visible page.
  const [summary, setSummary] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await api.fetchHistory({
        page,
        limit: 20,
        search: debouncedSearch,
        status: statusFilter,
        eventId: eventFilter,
        overlap: overlapOnly,
      });
      setHistory(res.data || []);
      setLastPage(res.pagination?.lastPage || 1);
      setTotal(res.pagination?.total || 0);
      setSummary(res.summary || null);
    } catch (err) {
      toast.error(err.message || "Failed to load game history");
    } finally {
      setLoadingHistory(false);
    }
  }, [api, page, debouncedSearch, statusFilter, eventFilter, overlapOnly]);

  useEffect(() => {
    if (mainTab === "history") loadHistory();
  }, [mainTab, loadHistory]);

  // The event list barely changes, so it is fetched once, the first time the
  // history tab opens. A failure leaves the filter on "All events" rather than
  // blocking the history itself.
  useEffect(() => {
    if (mainTab !== "history" || events !== null || !api.fetchHistoryEvents) return;
    api
      .fetchHistoryEvents()
      .then((res) => setEvents(res.data || []))
      .catch(() => setEvents([]));
  }, [mainTab, events, api]);

  const handleEventChange = (e) => {
    setEventFilter(e.target.value);
    setPage(1);
    setExpandedId(null);
  };

  const toggleOverlapOnly = () => {
    setOverlapOnly((on) => !on);
    setPage(1);
    setExpandedId(null);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(val);
    }, 400);
  };

  const handleStatusChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const copySessionId = (sessionId) => {
    navigator.clipboard?.writeText(sessionId);
    toast.success("Session ID copied");
  };

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-amber-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    {title}
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                        settings?.isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {settings?.isActive ? "Live" : "Disabled"}
                    </span>
                  </h1>
                  <p className="text-xs text-gray-400 mt-1">{description}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {/* Headline figures and daily trends */}
                {settings && (
                  <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard label="Total Sessions" value={settings.stats?.totalSessions ?? 0} icon="🎯" />
                      <StatCard
                        label="Completed Sessions"
                        value={settings.stats?.completedSessions ?? 0}
                        tone="text-emerald-600"
                        icon="🟢"
                      />
                      <StatCard
                        label="Points Awarded"
                        value={settings.stats?.totalPointsAwarded ?? 0}
                        suffix="PTS"
                        tone="text-violet-600"
                        icon="⚡"
                      />
                      <StatCard
                        label="Win Rate"
                        value={(settings.stats?.winRate ?? 0) + "%"}
                        tone="text-amber-600"
                        icon="🎖️"
                      />
                    </div>

                    {trends?.data?.length > 0 && (
                      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-50">
                          <div>
                            <h3 className="text-sm font-bold text-gray-800">{title} Performance Analytics</h3>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              Daily player engagement and reward points over time.
                            </p>
                          </div>
                          <div className="flex gap-1 bg-gray-50 rounded-xl p-1">
                            {[7, 14, 30].map((d) => (
                              <button
                                key={d}
                                onClick={() => setTrendDays(d)}
                                className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition ${
                                  trendDays === d ? "bg-amber-700 text-white shadow-sm" : "text-gray-500 hover:text-gray-800"
                                }`}
                              >
                                Last {d} Days
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 p-5">
                          <div className="border border-gray-100 rounded-2xl p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="text-xs font-semibold text-gray-700">Daily Players / Sessions</p>
                                <p className="text-[11px] text-gray-400">Unique players and completed rounds per day</p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-gray-800">
                                  {(trends.summary?.totalSessions ?? 0).toLocaleString()}
                                </p>
                                <p className="text-[11px] text-gray-400">
                                  Avg {trends.summary?.avgSessionsPerDay ?? 0}/day
                                </p>
                              </div>
                            </div>
                            <DailyBars data={trends.data} />
                          </div>

                          <div className="border border-gray-100 rounded-2xl p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="text-xs font-semibold text-gray-700">Daily Points Awarded</p>
                                <p className="text-[11px] text-gray-400">Total event points claimed per day</p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-violet-600">
                                  {(trends.summary?.totalPoints ?? 0).toLocaleString()}
                                  <span className="text-[11px] text-gray-400 font-semibold ml-1">PTS</span>
                                </p>
                                <p className="text-[11px] text-gray-400">
                                  Avg {trends.summary?.avgPointsPerDay ?? 0} pts/day
                                </p>
                              </div>
                            </div>
                            <DailyArea data={trends.data} />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="flex gap-1 bg-white border border-gray-100 rounded-xl p-1 shadow-sm">
                  <span className="px-4 py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white shadow-sm">
                    {selfLabel}
                  </span>
                  {switchTargets.map((target) => (
                    <Link
                      key={target.to}
                      to={target.to}
                      className="px-4 py-2 text-xs font-medium rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition"
                    >
                      {target.label}
                    </Link>
                  ))}
                </div>
                <button
                  onClick={mainTab === "settings" ? loadSettings : loadHistory}
                  disabled={loadingSettings || loadingHistory}
                  className="p-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-xl transition shadow-sm"
                  title="Refresh"
                >
                  <FiRefreshCw size={15} className={loadingSettings || loadingHistory ? "animate-spin text-blue-600" : ""} />
                </button>
              </div>
            </div>

            {/*
              The headline figures are rendered once, above, by the StatCard
              block that also carries Win Rate. A second three-card row used to
              sit here and showed the same numbers twice.
            */}

            {/* Main tabs */}
            <div className="flex gap-1 bg-white border border-gray-100 rounded-xl p-1 w-fit shadow-sm">
              {MAIN_TABS.map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setMainTab(tab.key)}
                    className={`flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-lg transition ${
                      mainTab === tab.key
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <TabIcon size={14} /> {tab.label}
                  </button>
                );
              })}
            </div>

            {/* ── Settings tab ─────────────────────────────────────────── */}
            {mainTab === "settings" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
                {loadingSettings || !form ? (
                  <div className="flex justify-center items-center py-16">
                    <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between pb-5 border-b border-gray-100">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700">{title} Status</h3>
                        <p className="text-xs text-gray-400 mt-0.5">Enable or disable this mini-game in the mobile app.</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-medium ${form.isActive ? "text-emerald-500" : "text-gray-400"}`}>
                          {form.isActive ? "Active (Enabled)" : "Inactive (Disabled)"}
                        </span>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, isActive: !form.isActive })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            form.isActive ? "bg-blue-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              form.isActive ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/*
                      Each scoring model owns its own settings, and only those.

                      A roll-based game (Tap, Boom Cards) configures a duration,
                      a break, and the point range its random target is drawn
                      from. A formula game (2048) replaces that range with the
                      score-to-points conversion and has no timer.

                      Slots and the wheel configure neither: their payouts come
                      from tiers and slices, and their own sections below carry
                      their break time. Showing these fields to them offered
                      settings the server ignores.
                    */}
                    {(isRollBasedGame || isFormulaGame) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {!isFormulaGame && (
                          <>
                            <Field
                              label="Game Duration (Seconds)"
                              value={form.gameTime}
                              min={5}
                              max={600}
                              hint="Default: 30 seconds per match session"
                              onChange={(e) => setForm({ ...form, gameTime: e.target.value })}
                            />
                            <Field
                              label="Break / Cooldown Time (Seconds)"
                              value={form.cooldownSeconds}
                              min={0}
                              max={86400}
                              hint="Time a player must wait before playing again"
                              onChange={(e) => setForm({ ...form, cooldownSeconds: e.target.value })}
                            />
                          </>
                        )}

                        {isFormulaGame ? (
                          <>
                            <Field
                              label="Score Per Unit"
                              value={form.scoreUnit}
                              min={1}
                              hint="How much score buys one payout unit"
                              onChange={(e) => setForm({ ...form, scoreUnit: e.target.value })}
                            />
                            <Field
                              label="Points Per Unit"
                              value={form.pointsPerUnit}
                              min={0}
                              hint="Event points paid for each unit of score"
                              onChange={(e) => setForm({ ...form, pointsPerUnit: e.target.value })}
                            />
                          </>
                        ) : (
                          <>
                            <Field
                              label="Minimum Reward Points"
                              value={form.minPoints}
                              min={1}
                              hint="Minimum score / points rewarded"
                              onChange={(e) => setForm({ ...form, minPoints: e.target.value })}
                            />
                            <Field
                              label="Maximum Reward Points"
                              value={form.maxPoints}
                              min={1}
                              hint="Maximum score / points rewarded"
                              onChange={(e) => setForm({ ...form, maxPoints: e.target.value })}
                            />
                          </>
                        )}
                      </div>
                    )}

                    {isWheelGame && (
                      <div className="mt-5 pt-5 border-t border-gray-100 space-y-6">
                        {/* The wheel, drawn from the very values being edited */}
                        <div className="flex flex-col lg:flex-row gap-6">
                          <div className="flex flex-col items-center shrink-0">
                            <FortuneWheel slices={(form.slices || []).map(Number)} highlight={testSpin} size={280} />
                            <button
                              type="button"
                              onClick={testSpinWheel}
                              disabled={spinning}
                              className="mt-4 px-4 py-2.5 text-xs font-bold rounded-xl bg-amber-400 text-gray-900 hover:bg-amber-300 disabled:opacity-60 transition"
                            >
                              {spinning ? "Spinning..." : "▶ Test Spin Simulator"}
                            </button>
                            <p className="text-[10px] text-gray-400 mt-2 text-center max-w-[240px]">
                              Previews the odds on screen. Costs no play and starts no cooldown.
                            </p>
                          </div>

                          <div className="flex-1">
                            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                              🎯 {(form.slices || []).length} Slices Point Configuration
                            </h3>
                            <p className="text-[11px] text-gray-400 mb-4">
                              The event points each slice awards, in the order they appear on the wheel.
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                              {(form.slices || []).map((value, i) => (
                                <div key={i} className="rounded-xl border border-gray-100 bg-gray-50/60 px-3 py-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase">Slice #{i}</span>
                                    <span
                                      className="w-2.5 h-2.5 rounded-full"
                                      style={{
                                        background: [
                                          "#ef4444","#3b82f6","#64748b","#10b981","#8b5cf6",
                                          "#f59e0b","#ec4899","#06b6d4","#475569","#eab308",
                                        ][i % 10],
                                      }}
                                    />
                                  </div>
                                  <div className="flex items-baseline gap-1">
                                    <input
                                      type="number"
                                      min={0}
                                      value={value}
                                      onChange={(e) => {
                                        const next = [...form.slices];
                                        next[i] = e.target.value;
                                        setForm({ ...form, slices: next });
                                      }}
                                      className="w-full bg-transparent text-base font-bold text-gray-800 focus:outline-none"
                                    />
                                    <span className="text-[10px] text-gray-400">pts</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 rounded-xl bg-amber-50 border border-amber-100 px-4 py-2.5">
                              <p className="text-[11px] text-amber-900">
                                Slices worth <span className="font-bold">0 pts</span> are the miss zones. How often each
                                prize comes up is set by the probabilities below, not by how many slices carry it.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Prize probabilities */}
                        <div>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1">
                            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                              % Win Probability Percentages (6 Tiers)
                            </h3>
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border ${
                                  wheelOddsTotal === 100
                                    ? "border-emerald-200 text-emerald-700 bg-emerald-50"
                                    : "border-amber-200 text-amber-700 bg-amber-50"
                                }`}
                              >
                                Total: {wheelOddsTotal}%
                              </span>
                              <button
                                type="button"
                                onClick={autoBalance}
                                className="px-3 py-1.5 text-[11px] font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                              >
                                ⚡ Auto-Balance
                              </button>
                              <button
                                type="button"
                                onClick={resetWheelDefaults}
                                className="px-3 py-1.5 text-[11px] font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                              >
                                ↺ Reset Defaults
                              </button>
                            </div>
                          </div>
                          <p className="text-[11px] text-gray-400 mb-4">
                            How often players land on each reward. These are weights drawn against their own sum, so
                            they need not total exactly 100 — Auto-Balance scales them if you want it exact.
                          </p>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <PercentSlider
                              label="0 PTS (Loss / Miss)"
                              value={form.lossPercentage}
                              color="#a16207"
                              hint="Baseline miss chance"
                              onChange={(v) => setForm({ ...form, lossPercentage: v })}
                            />
                            <PercentSlider
                              label="+10 Points"
                              value={form.tenPointsPercentage}
                              color="#2563eb"
                              hint="Standard common win"
                              onChange={(v) => setForm({ ...form, tenPointsPercentage: v })}
                            />
                            <PercentSlider
                              label="+50 Points"
                              value={form.fiftyPointsPercentage}
                              color="#059669"
                              hint="Uncommon win"
                              onChange={(v) => setForm({ ...form, fiftyPointsPercentage: v })}
                            />
                            <PercentSlider
                              label="+100 Points"
                              value={form.hundredPointsPercentage}
                              color="#7c3aed"
                              hint="Rare reward tier"
                              onChange={(v) => setForm({ ...form, hundredPointsPercentage: v })}
                            />
                            <PercentSlider
                              label="+500 Points"
                              value={form.fiveHundredPointsPercentage}
                              color="#db2777"
                              hint="Epic jackpot slice"
                              onChange={(v) => setForm({ ...form, fiveHundredPointsPercentage: v })}
                            />
                            <PercentSlider
                              label="👑 +1000 Points (Grand)"
                              value={form.thousandPointsPercentage}
                              color="#d97706"
                              hint="Ultra-rare grand jackpot"
                              accentBg="bg-amber-50/40"
                              onChange={(v) => setForm({ ...form, thousandPointsPercentage: v })}
                            />
                          </div>
                        </div>

                        {/* Spin rules */}
                        <div className="border-t border-gray-100 pt-5">
                          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                            General Rules &amp; App Controls
                          </h3>
                          <p className="text-[11px] text-gray-400 mb-4">
                            Spin animation length and the break between plays.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field
                              label="Spin Animation Duration (Seconds)"
                              value={form.spinTime}
                              min={1}
                              max={60}
                              hint="How long the wheel spins before it stops"
                              onChange={(e) => setForm({ ...form, spinTime: e.target.value })}
                            />
                            <Field
                              label="Cooldown Period (Seconds)"
                              value={form.cooldownSeconds}
                              min={0}
                              max={86400}
                              hint="Wait between plays. 0 for none"
                              onChange={(e) => setForm({ ...form, cooldownSeconds: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {isSlotsGame && (
                      <div className="mt-5 pt-5 border-t border-gray-100">
                        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                          Point Combination Payouts
                        </h3>
                        <p className="text-[11px] text-gray-400 mb-4">
                          What each outcome pays. The jackpot is the most a single spin can award.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                          <Field label="7️⃣ 7️⃣ 7️⃣ Jackpot Points" value={form.jackpotPoints} min={0}
                            hint="Payout when 3 Sevens align"
                            onChange={(e) => setForm({ ...form, jackpotPoints: e.target.value })} />
                          <Field label="🍫 🍫 🍫 Triple Bar Points" value={form.tripleBarPoints} min={0}
                            hint="Payout for 3 Bar symbols"
                            onChange={(e) => setForm({ ...form, tripleBarPoints: e.target.value })} />
                          <Field label="🍒 🍒 🍒 Triple Cherry Points" value={form.tripleCherryPoints} min={0}
                            hint="Payout for 3 Cherry symbols"
                            onChange={(e) => setForm({ ...form, tripleCherryPoints: e.target.value })} />
                          <Field label="🍋 🍉 Other Triple Match Points" value={form.otherTriplePoints} min={0}
                            hint="Payout for 3 Lemons or Watermelons"
                            onChange={(e) => setForm({ ...form, otherTriplePoints: e.target.value })} />
                          <Field label="🍒 🍒 Any 2 Match Points" value={form.match2Points} min={0}
                            hint="Payout when 2 symbols match in a spin"
                            onChange={(e) => setForm({ ...form, match2Points: e.target.value })} />
                        </div>

                        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mt-6 mb-1">
                          Spin Win Probability Distribution
                        </h3>
                        <p className="text-[11px] text-gray-400 mb-4">
                          The chance of each outcome per spin. These are weights, so they need not total exactly
                          100 — they are drawn against their own sum.{" "}
                          <span className={oddsTotal === 100 ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"}>
                            Currently {oddsTotal}%
                          </span>
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                          <Field label="7️⃣ 7️⃣ 7️⃣ Jackpot Chance (%)" value={form.jackpotPercentage} min={0} max={100}
                            onChange={(e) => setForm({ ...form, jackpotPercentage: e.target.value })} />
                          <Field label="🍫 🍫 🍫 Triple Bar Chance (%)" value={form.tripleBarPercentage} min={0} max={100}
                            onChange={(e) => setForm({ ...form, tripleBarPercentage: e.target.value })} />
                          <Field label="🍒 🍒 🍒 Triple Cherry Chance (%)" value={form.tripleCherryPercentage} min={0} max={100}
                            onChange={(e) => setForm({ ...form, tripleCherryPercentage: e.target.value })} />
                          <Field label="🍋 🍉 Match 3 Fruits Chance (%)" value={form.otherTriplePercentage} min={0} max={100}
                            onChange={(e) => setForm({ ...form, otherTriplePercentage: e.target.value })} />
                          <Field label="🍒 🍒 Any 2 Match Chance (%)" value={form.match2Percentage} min={0} max={100}
                            onChange={(e) => setForm({ ...form, match2Percentage: e.target.value })} />
                          <Field label="❌ No Match / Loss Chance (%)" value={form.lossPercentage} min={0} max={100}
                            onChange={(e) => setForm({ ...form, lossPercentage: e.target.value })} />
                        </div>

                        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mt-6 mb-1">
                          Session Rules
                        </h3>
                        {/*
                          No break time: a round is the spins below, all
                          decided when it starts, so rounds run back to back.
                        */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-4">
                          <Field label="Total Spins Per Round" value={form.totalSpins} min={1} max={50}
                            hint="All spins are decided by the server when the round starts"
                            onChange={(e) => setForm({ ...form, totalSpins: e.target.value })} />
                        </div>
                      </div>
                    )}

                    {isFormulaGame && (
                      <div className="mt-5 pt-5 border-t border-gray-100">
                        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                          Anti-Cheat Limits
                        </h3>
                        <p className="text-[11px] text-gray-400 mb-4">
                          Enforced by the server on every submit. A round breaking any of these is rejected and
                          earns nothing.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                          <Field
                            label="Max Points Per Round"
                            value={form.maxPointsPerRound}
                            min={1}
                            hint="Highest payout one round may claim"
                            onChange={(e) => setForm({ ...form, maxPointsPerRound: e.target.value })}
                          />
                          <Field
                            label="Max Score Per Round"
                            value={form.maxScorePerRound}
                            min={1}
                            hint="A score above this is treated as impossible"
                            onChange={(e) => setForm({ ...form, maxScorePerRound: e.target.value })}
                          />
                          <Field
                            label="Minimum Play Time (Seconds)"
                            value={form.minPlaySeconds}
                            min={0}
                            max={3600}
                            hint="Rounds closed faster than this are refused"
                            onChange={(e) => setForm({ ...form, minPlaySeconds: e.target.value })}
                          />
                        </div>

                        <div className="mt-4 rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
                          <p className="text-xs text-blue-900">
                            <span className="font-semibold">Payout:</span>{" "}
                            <span className="font-mono">
                              points = floor(score / {form.scoreUnit || 0}) &times; {form.pointsPerUnit || 0}
                            </span>
                          </p>
                          <p className="text-xs text-blue-900 mt-1">
                            A score of {sampleScore.toLocaleString()} pays{" "}
                            <span className="font-semibold">{samplePoints.toLocaleString()}</span> event points.
                          </p>
                          {settings?.ceilings?.effectiveMaxScore != null && (
                            <p className="text-[11px] text-blue-800/80 mt-2">
                              {settings.ceilings.note} The{" "}
                              {settings.ceilings.bindingCap === "maxPointsPerRound"
                                ? "points cap"
                                : "score cap"}{" "}
                              is the limit that applies first.
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end pt-2">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition shadow-sm shadow-blue-600/20"
                      >
                        <FiSave size={14} /> {saving ? "Saving..." : `Save ${title} Settings`}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── History tab ──────────────────────────────────────────── */}
            {mainTab === "history" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3 border-b border-gray-100">
                  <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                    <div className="relative w-full sm:w-72">
                      <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                      <input
                        value={search}
                        onChange={handleSearchChange}
                        placeholder="Search by Player, User ID, or Session ID..."
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      />
                    </div>
                    <select
                      value={eventFilter}
                      onChange={handleEventChange}
                      aria-label="Filter by event"
                      className="w-full sm:w-56 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="">All events</option>
                      {(events || []).map((ev) => (
                        <option key={ev.id} value={ev.id}>
                          {ev.name}
                          {ev.status === "running" ? " (running)" : ""}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={toggleOverlapOnly}
                      aria-pressed={overlapOnly}
                      title="Only sessions played while the same player had another game's session open"
                      className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-semibold border whitespace-nowrap transition ${
                        overlapOnly
                          ? "bg-rose-600 text-white border-rose-600 shadow-sm"
                          : "bg-white text-rose-600 border-rose-200 hover:bg-rose-50"
                      }`}
                    >
                      <FiAlertTriangle size={13} />
                      Same-time play
                    </button>
                  </div>
                  <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
                    {STATUS_TABS.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => handleStatusChange(tab.key)}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                          statusFilter === tab.key
                            ? "bg-blue-600 text-white shadow-sm"
                            : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Totals for everything the filters match — the whole event,
                    say — not just the twenty rows on screen. */}
                {summary && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 border-b border-gray-100 bg-gray-50/40">
                    <StatCard label="Sessions" value={summary.sessions} icon="🎮" />
                    <StatCard label="Players" value={summary.players} icon="👤" />
                    <StatCard
                      label="Avg Duration"
                      value={formatDuration(summary.avgDurationSeconds)}
                      icon="⏱️"
                    />
                    <StatCard label="Points Paid" value={summary.points} suffix="PTS" tone="text-violet-600" icon="⭐" />
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-[11px] text-gray-400 uppercase tracking-wide">
                        <th className="px-5 py-3">Player</th>
                        <th className="px-5 py-3">Session ID</th>
                        <th className="px-5 py-3">{scoreLabel}</th>
                        <th className="px-5 py-3">Points</th>
                        <th className="px-5 py-3">Start Time</th>
                        <th className="px-5 py-3">End Time</th>
                        <th className="px-5 py-3" title="From session start to end">
                          Duration
                        </th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3 text-center">Info</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {loadingHistory ? (
                        <tr>
                          <td colSpan={9} className="py-12 text-center text-gray-400 text-xs">
                            Loading…
                          </td>
                        </tr>
                      ) : history.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-12 text-center text-gray-400 text-xs">
                            {overlapOnly
                              ? "Nobody played this game while another game's session was open."
                              : "No sessions found."}
                          </td>
                        </tr>
                      ) : (
                        history.map((row) => (
                          <React.Fragment key={row.id}>
                            <tr className="hover:bg-gray-50 transition">
                              <td className="px-5 py-3.5">
                                <p className="font-medium text-gray-800">{row.username || "—"}</p>
                                <p className="text-[11px] text-gray-400">ID: #{row.userId}</p>
                              </td>
                              <td className="px-5 py-3.5">
                                <button
                                  onClick={() => copySessionId(row.sessionId)}
                                  className="flex items-center gap-1.5 text-[11px] font-mono text-gray-500 hover:text-blue-600 transition"
                                  title={row.sessionId}
                                >
                                  <span className="truncate max-w-[140px]">{row.sessionId}</span>
                                  <FiCopy size={11} className="shrink-0" />
                                </button>
                              </td>
                              <td className="px-5 py-3.5 text-gray-600">{row.score ?? "—"}</td>
                              <td className="px-5 py-3.5">
                                <span className="inline-block bg-violet-50 text-violet-600 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                  +{row.claimedPoints || 0} / {row.targetPoints} PTS
                                </span>
                              </td>
                              <td className="px-5 py-3.5">
                                <TimeCell value={row.startedAt} />
                              </td>
                              <td className="px-5 py-3.5">
                                {/* An open round has no end yet; an abandoned
                                    one never will. Neither is a missing value. */}
                                <TimeCell
                                  value={row.completedAt}
                                  empty={row.status === "started" ? "Not finished" : "—"}
                                />
                              </td>
                              <td className="px-5 py-3.5 text-gray-700 font-medium tabular-nums whitespace-nowrap">
                                {formatDuration(row.durationSeconds)}
                              </td>
                              <td className="px-5 py-3.5">
                                <span
                                  className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full border capitalize ${
                                    STATUS_BADGE[row.status] || "bg-gray-100 text-gray-500 border-gray-200"
                                  }`}
                                >
                                  {row.status}
                                </span>
                                {row.overlaps?.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedId(row.id)}
                                    title={`Played at the same time as: ${row.overlaps
                                      .map((o) => o.gameName)
                                      .join(", ")}`}
                                    className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-rose-600 hover:text-rose-700 whitespace-nowrap"
                                  >
                                    <FiAlertTriangle size={11} />
                                    Same time ×{row.overlaps.length}
                                  </button>
                                )}
                              </td>
                              <td className="px-5 py-3.5 text-center">
                                <button
                                  onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                                  className="text-gray-400 hover:text-blue-600 transition"
                                >
                                  <FiInfo size={16} />
                                </button>
                              </td>
                            </tr>
                            {expandedId === row.id && (
                              <tr className="bg-gray-50/70">
                                <td colSpan={9} className="px-5 py-3 text-xs text-gray-600">
                                  {/* Start, end and duration are on the row
                                      itself now; this keeps what is not. */}
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div>
                                      <p className="text-gray-400">Event</p>
                                      <p className="font-medium">{row.eventName || "—"}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-400">Duration / Game Time</p>
                                      <p className="font-medium">
                                        {formatDuration(row.durationSeconds)} / {row.gameTime}s
                                      </p>
                                    </div>
                                    {row.disqualifyReason && (
                                      <div className="col-span-2 sm:col-span-4">
                                        <p className="text-gray-400">Disqualify Reason</p>
                                        <p className="font-medium text-rose-600">{row.disqualifyReason}</p>
                                      </div>
                                    )}
                                    {row.overlaps?.length > 0 && (
                                      <div className="col-span-2 sm:col-span-4">
                                        <p className="text-gray-400 mb-1.5 flex items-center gap-1.5">
                                          <FiAlertTriangle size={12} className="text-rose-500" />
                                          Played at the same time as — this session ran{" "}
                                          {formatClock(row.startedAt)} → {formatClock(row.completedAt)}
                                        </p>
                                        <div className="rounded-lg border border-rose-100 bg-white divide-y divide-rose-50">
                                          {row.overlaps.map((o) => (
                                            <div
                                              key={o.id}
                                              className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1"
                                            >
                                              <span className="font-semibold text-gray-800">{o.gameName}</span>
                                              <span className="font-mono text-[11px] text-gray-500 break-all">
                                                {o.sessionId}
                                              </span>
                                              <span className="tabular-nums">
                                                {formatClock(o.startedAt)} → {formatClock(o.completedAt)}
                                              </span>
                                              <span className="tabular-nums">{formatDuration(o.durationSeconds)}</span>
                                              <span className="font-semibold text-violet-600">
                                                +{o.claimedPoints} PTS
                                              </span>
                                              <span className="capitalize text-gray-500">{o.status}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 && (
                  <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <span>
                      Showing {history.length} of {total} sessions
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        disabled={page <= 1 || loadingHistory}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Previous
                      </button>
                      <span className="px-2 font-medium">
                        Page {page} of {lastPage}
                      </span>
                      <button
                        disabled={page >= lastPage || loadingHistory}
                        onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                        className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MiniGamePage;
